import { computed, toValue } from "vue";

import { toBoolean } from "#ce/utils/props";

/**
 * Returns the ARIA role a list of these items exposes, and the two facts it is derived from.
 * `listRole` is `listbox`, `tree`, or undefined for the generic semantics a list keeps when it
 * claims neither.
 *
 * A listbox may own only options, so a readonly (menu-style) list, and a list holding a navigation
 * or disclosure-branch row, claim no role.
 *
 * @param {object} source
 * @param {import('vue').MaybeRefOrGetter<Array|undefined>} source.items - the rows the list renders.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.readonly - whether the
 *   list only navigates.
 * @param {import('vue').MaybeRefOrGetter<boolean|string|undefined>} source.tree - whether it renders
 *   branch rows.
 * @param {(item: unknown) => boolean} source.isHeader
 * @param {(item: unknown) => object|undefined} source.getItemProps
 * @returns {{ listRole: import('vue').ComputedRef<string|undefined>, isListbox: import('vue').ComputedRef<boolean>, isTree: import('vue').ComputedRef<boolean> }}
 */
export function useListRole({ items, readonly, tree, isHeader, getItemProps }) {
  // Not an option: a row the panel marks `selectable: false` navigates instead of selecting, and a
  // branch row with an interactive caret carries a disclosure button, which an ARIA option would
  // hide from assistive technology (descendants of an option are presentational).
  function isOptionRow(item) {
    const itemProps = getItemProps(item);

    return itemProps?.selectable !== false && !toBoolean(itemProps?.appendCaretInteractive);
  }

  const isTree = computed(() => toBoolean(toValue(tree)));
  const isListbox = computed(() => {
    const rows = toValue(items) ?? [];

    // A listbox owns options, so at least one is required. `every` is vacuously true of no rows,
    // and a search that matched nothing still renders its section headers.
    const options = rows.filter((item) => !isHeader(item));

    return options.length > 0 && !toBoolean(toValue(readonly)) && options.every(isOptionRow);
  });

  const listRole = computed(() => {
    if (isTree.value) {
      return "tree";
    }

    return isListbox.value ? "listbox" : undefined;
  });

  return { listRole, isListbox, isTree };
}
